import { logEvent, ProductEvent } from "@classdojo/log-client";
import { useCallback, useContext } from "react";
import { EventMetadataContext } from "./EventMetadata";

export type EventLogger = (event: ProductEvent) => unknown;

/**
 * Hook to log events with automatically inherited metadata from EventMetadata context.
 * This allows manual logEvent calls to inherit metadata from parent
 * EventMetadata components.
 *
 * @example
 * // At a high level, wrap with EventMetadata:
 * <EventMetadata districtId={districtId}>
 *   <ChildComponents />
 * </EventMetadata>
 *
 * // In a child component:
 * const logEvent = useEventLogger();
 * logEvent({
 *   eventName: "teach.district.announcements.view",
 *   metadata: { listsCount: 5 }, // districtId auto-inherited!
 * });
 *
 * @important This hook uses React Context, NOT DOM traversal.
 *
 * Unlike automatic click events (which crawl the DOM tree to gather `data-event-metadata`
 * attributes), this hook only inherits metadata from `EventMetadata` React components.
 *
 * If you add `data-event-metadata` directly to DOM elements without using the `EventMetadata`
 * component, that metadata will be picked up by automatic click events but NOT by this hook.
 *
 * To ensure consistency between automatic and manual events, always use the `EventMetadata`
 * component (which provides both DOM attributes and React context).
 */
export function useEventLogger(eventLogger: EventLogger = logEvent) {
  const contextMetadata = useContext(EventMetadataContext);

  return useCallback(
    (event: Omit<ProductEvent, "metadata"> & { metadata?: Record<string, unknown> }) => {
      eventLogger({
        ...event,
        metadata: { ...contextMetadata, ...event.metadata },
      });
    },
    [contextMetadata, eventLogger],
  );
}
